import { useEffect, useRef, useState } from "preact/hooks";
import { patchMe } from "../../../api/client";
import type { User } from "../../../api/types";
import { setMe } from "../../../state/auth";
import { toast, toastError } from "../../../state/toasts";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { TextInput } from "../../components/Field";
import { cx } from "../../cx";
import { useEscape } from "../../hooks/hooks";
import { DISPLAY_NAME_MAX, DISPLAY_NAME_MIN, validateDisplayName } from "../../../app/validation";

/** Display name, `@username` / Guest badge and the inline rename form. Height is the same in both modes. */
export function NameEditor({ user }: { user: User }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(user.displayName);
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  const wasEditing = useRef(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // Focus follows the mode: into the field when editing starts, back to "Rename" when it ends.
  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    } else if (wasEditing.current) {
      rowRef.current?.querySelector("button")?.focus();
    }
    wasEditing.current = editing;
  }, [editing]);

  const nameError = validateDisplayName(draft);
  const unchanged = nameError === null && draft.trim() === user.displayName;

  const cancel = () => {
    if (!busy) setEditing(false);
  };
  useEscape(cancel, editing);

  const startEditing = () => {
    setDraft(user.displayName);
    setEditing(true);
  };

  const save = async () => {
    if (busy || nameError !== null || unchanged) return;
    setBusy(true);
    try {
      setMe(await patchMe(draft.trim()));
      toast("Display name updated.", "success");
      if (alive.current) setEditing(false);
    } catch (e) {
      toastError(e);
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  return (
    <div class="profile__who">
      <div class="profile__name-row" ref={rowRef}>
        {editing ? (
          <form
            class="profile__rename"
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            <TextInput
              value={draft}
              onValue={setDraft}
              ariaLabel="Display name"
              autoComplete="nickname"
              invalid={nameError !== null}
              disabled={busy}
              inputRef={inputRef}
              class="profile__rename-input"
            />
            <Button type="submit" variant="primary" busy={busy} disabled={nameError !== null || unchanged}>
              Save
            </Button>
            <Button variant="ghost" disabled={busy} onClick={cancel}>
              Cancel
            </Button>
          </form>
        ) : (
          <>
            <h2 class="profile__name" title={user.displayName}>
              {user.displayName}
            </h2>
            <Button size="sm" onClick={startEditing}>
              Rename
            </Button>
          </>
        )}
      </div>
      <div class={cx("profile__sub", editing && nameError !== null && "is-error")} aria-live="polite">
        {editing ? (
          <span>
            {nameError ?? `${DISPLAY_NAME_MIN}-${DISPLAY_NAME_MAX} printable characters. Enter saves, Esc cancels.`}
          </span>
        ) : (
          <>
            {user.username !== undefined && <span class="profile__username">@{user.username}</span>}
            {user.isGuest && <Badge>Guest</Badge>}
          </>
        )}
      </div>
    </div>
  );
}
